import React from "react";

import { Table, TableContainer, TableHead } from "@material-ui/core";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import { requestAuditLog } from "actions/auditLog";

import { AuditRow } from "components/AuditLog/AuditRow";
import { CenteredGreedy } from "components/Layout";
import LoadingSpinner from "components/LoadingSpinner";

const LoadingWrapper = styled(CenteredGreedy)`
  padding: ${({ theme }) => theme.space[2]}px;
`;

export const AuditLogTable = ({ dataType, dataId, queryParams }) => {
  const auditActions = useSelector(state => state.auditLog)?.[dataType]?.[
    dataId
  ];
  const dispatch = useDispatch();
  React.useEffect(() => {
    dispatch(requestAuditLog(dataType, dataId, queryParams));
  }, [dataType, dataId, dispatch, queryParams]);

  if (auditActions === null) {
    return (
      <CenteredGreedy>There was an error fetching the history.</CenteredGreedy>
    );
  } else if (typeof auditActions === "undefined") {
    return (
      <LoadingWrapper>
        <LoadingSpinner subjectName="Changes" />
      </LoadingWrapper>
    );
  }

  // process audit actions to include previous changes across disparate change payloads,
  // ensuring changes are compared against the last provided value in the history stream.
  // e.g. history entry just after related history entry shouldn't show the whole data set, only the items that actually changed.
  //
  // sample set of changes pulled out from auditActions to help illustrate:
  //   0 -> changes: {id: 89, …, short_code_auctions_plus: "APSC", …}
  //   1 -> changes: {deployment_business_email_recipient: 110}
  //   2 -> changes: {id: 89, …, primary_contact_email: email@example.com, …, short_code_auctions_plus: null, …}
  //   3 -> changes: {business_registration: 56}
  //   4 -> changes: {id: 89, …, primary_contact_email: null, …}
  // where comparing 0 against 1 for changes isn't right, would show every field as changed,
  // it should instead be only showing short_code_auctions_plus as changed (when compared against 2, or the last entry with this value, if any).
  //
  let combinedPreviousChanges = {};
  // audit log array is in reverse order, so process it in reverse to get the previous changes right,
  // copy first to avoid mutating state.
  const auditActionsWithPrevious = [...auditActions]
    .reverse()
    .map(action => {
      const actionWithPrevious = {
        ...action,
        previousChanges: combinedPreviousChanges,
      };
      combinedPreviousChanges = {
        ...combinedPreviousChanges,
        ...action.changes,
      };
      return actionWithPrevious;
    })
    .reverse();

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Timestamp</TableCell>
            <TableCell>User</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Info</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {auditActionsWithPrevious.map((action, idx) => (
            <AuditRow
              action={action}
              key={`audit-row-${idx}`}
              dataType={dataType}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
